import { TrendingDown } from 'lucide-react';
import type { AlertDetailResponse } from '@/types/alerts';
import {
  EvidenceRow,
  HeadlineLine,
  MissingContextLine,
  RendererShell,
  getNum,
  getStr,
} from './_shared';

/**
 * KE-003 — KML Trend Decline.
 *
 * "Vehicle's kilometres-per-litre trend is declining over a multi-period
 * window." Sustained decline (rather than a single-point spike) suggests
 * gradual fuel diversion, progressive maintenance issues, or odometer
 * manipulation creeping in over time.
 *
 * Evidence keys (from domain/fraud_rules/kml_rules.py KMLTrendDeclineRule
 * .create_alert): trend_direction, trend_percentage (signed; negative
 * means declining), latest_kml, oldest_kml, data_points.
 */
export default function KmlTrendRenderer(
  evidence: Record<string, unknown> | null | undefined,
  alert: AlertDetailResponse,
): JSX.Element {
  const direction = getStr(evidence, 'trend_direction') ?? 'declining';
  const trendPct = getNum(evidence, 'trend_percentage');
  const latest = getNum(evidence, 'latest_kml');
  const oldest = getNum(evidence, 'oldest_kml');
  const dataPoints = getNum(evidence, 'data_points');
  const absPct = trendPct !== undefined ? Math.abs(trendPct) : undefined;
  const vehicleReg = alert.asset_token;

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <TrendingDown className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
        <HeadlineLine>
          Fuel-efficiency trend is{' '}
          <span className="text-red-700">{direction}</span>
          {absPct !== undefined ? `: ${absPct.toFixed(1)}% drop` : ''}
          {dataPoints !== undefined ? ` across ${dataPoints} periods` : ''}.
        </HeadlineLine>
      </div>

      <div className="flex items-baseline justify-around gap-3 rounded-md border bg-muted/20 px-3 py-3 text-sm tabular-nums">
        <div className="text-center">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Earliest
          </div>
          <div className="text-lg font-bold">
            {oldest !== undefined ? `${oldest.toFixed(2)} km/L` : '—'}
          </div>
        </div>
        <TrendingDown className="h-5 w-5 text-red-600" />
        <div className="text-center">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Latest
          </div>
          <div className="text-lg font-bold">
            {latest !== undefined ? `${latest.toFixed(2)} km/L` : '—'}
          </div>
        </div>
        {absPct !== undefined && (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
            −{absPct.toFixed(1)}%
          </span>
        )}
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Vehicle">
          <span className="font-mono text-xs">{vehicleReg ?? '—'}</span>
        </EvidenceRow>
        <EvidenceRow label="Trend direction">{direction}</EvidenceRow>
        <EvidenceRow label="Magnitude">
          {trendPct !== undefined ? `${trendPct.toFixed(1)}%` : '—'}
        </EvidenceRow>
        <EvidenceRow label="Latest KML">
          {latest !== undefined ? `${latest.toFixed(2)} km/L` : '—'}
        </EvidenceRow>
        <EvidenceRow label="Earliest KML">
          {oldest !== undefined ? `${oldest.toFixed(2)} km/L` : '—'}
        </EvidenceRow>
        <EvidenceRow label="Periods analysed">
          {dataPoints ?? '—'}
        </EvidenceRow>
      </div>

      {trendPct === undefined && latest === undefined && (
        <MissingContextLine message="Trend metrics are missing — see raw evidence below." />
      )}
    </RendererShell>
  );
}
